/**
 * Data downloader for pre-caching critical data for offline use
 * Automatically downloads customers, products, services, and projects when online
 */

import { isOnline } from "../sync/network-detector";
import {
  downloadCustomers,
  downloadProducts,
  downloadServices,
  getCachedOrFetchCustomers,
  getCachedOrFetchProducts,
  getCachedOrFetchServices,
  isCacheStale,
} from "./cache-manager";

export interface DownloadProgress {
  stage: "customers" | "products" | "services" | "complete";
  progress: number; // 0-100
  message: string;
}

export interface DownloadResult {
  success: boolean;
  customersCount: number;
  productsCount: number;
  servicesCount: number;
  error?: string;
}

type ProgressCallback = (progress: DownloadProgress) => void;

/**
 * Download all data for offline use
 */
export async function downloadAllData(onProgress?: ProgressCallback): Promise<DownloadResult> {
  const result: DownloadResult = {
    success: false,
    customersCount: 0,
    productsCount: 0,
    servicesCount: 0,
    projectsCount: 0,
  };

  try {
    // Check if online
    if (!isOnline()) {
      throw new Error("Cannot download data while offline");
    }

    // Download customers
    onProgress?.({
      stage: "customers",
      progress: 0,
      message: "Downloading customers...",
    });

    const customersResult = await downloadCustomers();
    result.customersCount = customersResult.count;

    onProgress?.({
      stage: "customers",
      progress: 25,
      message: `${customersResult.count} customers downloaded`,
    });

    // Download products
    onProgress?.({
      stage: "products",
      progress: 25,
      message: "Downloading products...",
    });

    const productsResult = await downloadProducts();
    result.productsCount = productsResult.count;

    onProgress?.({
      stage: "products",
      progress: 50,
      message: `${productsResult.count} products downloaded`,
    });

    // Download services
    onProgress?.({
      stage: "services",
      progress: 50,
      message: "Downloading services...",
    });

    const servicesResult = await downloadServices();
    result.servicesCount = servicesResult.count;

    onProgress?.({
      stage: "services",
      progress: 75,
      message: `${servicesResult.count} services downloaded`,
    });

    // Mark as complete
    onProgress?.({
      stage: "complete",
      progress: 100,
      message: "All data downloaded successfully",
    });

    result.success = true;
    return result;
  } catch (error) {
    console.error("[DataDownloader] Error downloading data:", error);
    result.error = error instanceof Error ? error.message : "Failed to download data";
    return result;
  }
}

/**
 * Check if data needs to be refreshed (older than specified age)
 */
export async function shouldRefreshData(maxAgeMs = 3600000): Promise<boolean> {
  try {
    const isStale = await isCacheStale("customers");
    return isStale;
  } catch (error) {
    console.error("[DataDownloader] Error checking refresh status:", error);
    return true; // Refresh on error
  }
}

/**
 * Get cache statistics
 */
export async function getCacheStats(): Promise<{
  customers: number;
  products: number;
  services: number;
  lastSync: Date | null;
  cacheSize: number;
}> {
  try {
    const [customers, products, services] = await Promise.all([
      getCachedOrFetchCustomers(),
      getCachedOrFetchProducts(),
      getCachedOrFetchServices(),
    ]);

    // Estimate cache size (rough calculation)
    const cacheSize =
      JSON.stringify(customers).length +
      JSON.stringify(products).length +
      JSON.stringify(services).length;

    return {
      customers: customers.length,
      products: products.length,
      services: services.length,
      lastSync: null, // We'll set this from cache metadata
      cacheSize: Math.round(cacheSize / 1024), // KB
    };
  } catch (error) {
    console.error("[DataDownloader] Error getting cache stats:", error);
    return {
      customers: 0,
      products: 0,
      services: 0,
      lastSync: null,
      cacheSize: 0,
    };
  }
}

/**
 * Auto-download data when online (with smart refresh)
 */
export async function autoDownloadData(onProgress?: ProgressCallback): Promise<DownloadResult> {
  try {
    if (!isOnline()) {
      console.log("[DataDownloader] Offline, skipping auto-download");
      return {
        success: false,
        customersCount: 0,
        productsCount: 0,
        servicesCount: 0,
        error: "Device is offline",
      };
    }

    // Check if refresh is needed (1 hour staleness)
    const needsRefresh = await shouldRefreshData(3600000);

    if (!needsRefresh) {
      console.log("[DataDownloader] Cache is fresh, skipping download");
      const stats = await getCacheStats();
      return {
        success: true,
        customersCount: stats.customers,
        productsCount: stats.products,
        servicesCount: stats.services,
      };
    }

    console.log("[DataDownloader] Cache stale, downloading fresh data...");
    return await downloadAllData(onProgress);
  } catch (error) {
    console.error("[DataDownloader] Auto-download error:", error);
    return {
      success: false,
      customersCount: 0,
      productsCount: 0,
      servicesCount: 0,
      error: error instanceof Error ? error.message : "Auto-download failed",
    };
  }
}
